/**
 * Range1925Strategy
 * Handles '19 to '25 timeline range (2019-2025)
 * Shows content for all tabs with period-type awareness for Income Statement
 */

import type { ContentStrategy, PeriodType, TabName } from '@/types';
import { DOM_SELECTORS, IMAGE_PATHS } from '@/utils/constants';
import {
  hideElements,
  removeSections,
  createImageHTML,
  createQuarterlyIncomeImageHTML,
  injectContent,
  hideNoDataMessage,
  showKeyStatsContent,
} from './ContentHelper';

/**
 * Strategy for 2019-2025 timeline range
 */
export class Range1925Strategy implements ContentStrategy {
  /**
   * Apply strategy for 2019-2025 timeline
   */
  public apply(tab: TabName, periodType: PeriodType): void {
    switch (tab) {
      case 'key-stats':
        this.applyKeyStatsContent();
        break;

      case 'income-statement':
        this.applyIncomeStatementContent(periodType);
        break;

      case 'balance-sheet':
        this.applyBalanceSheetContent();
        break;

      case 'cash-flow':
        this.applyCashFlowContent();
        break;

      default:
        this.hideAllContent();
    }
  }

  /**
   * Apply Key Stats content: Restore visibility of original content
   * Remove any injected messages and show the original Key Stats HTML from the view
   */
  private applyKeyStatsContent(): void {
    // Remove all financial statement sections that might have been injected
    removeSections([
      DOM_SELECTORS.CAP_SECTION_2020_2023,
      DOM_SELECTORS.VAL_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.INCOME_SECTION_1323,
      DOM_SELECTORS.BALANCE_SECTION_2020_2023,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_1323,
      DOM_SELECTORS.CASH_SECTION_2020_2023,
      DOM_SELECTORS.CASH_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1323,
    ]);

    // Hide any "No data available" message that might be visible
    hideNoDataMessage();

    // Show the original Key Stats content from the view
    showKeyStatsContent();
  }

  /**
   * Apply Income Statement content with period-type awareness
   */
  private applyIncomeStatementContent(periodType: PeriodType): void {
    // Remove other financial statement sections first (cleanup)
    removeSections([
      DOM_SELECTORS.INCOME_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_2020_2023,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_2020_2023,
      DOM_SELECTORS.CASH_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1323,
    ]);

    // Create content based on period type
    let incomeHTML: string;

    if (periodType === 'Quarterly') {
      // Quarterly has special flex layout
      incomeHTML = createQuarterlyIncomeImageHTML(
        IMAGE_PATHS.INCOME_1925_QUARTERLY,
        'income-statement-section-1925'
      );
    } else {
      // Annual (default)
      incomeHTML = createImageHTML(
        IMAGE_PATHS.INCOME_1925_ANNUAL,
        'income-statement-section-1925'
      );
    }

    // Inject content with defensive check
    const success = injectContent(DOM_SELECTORS.MAIN_SECTION, incomeHTML);
    if (!success) {
      console.error('[Range1925Strategy] Failed to inject income statement content - .main-section not found');
    }
  }

  /**
   * Apply Balance Sheet content (no period type variation for 2019-2025)
   */
  private applyBalanceSheetContent(): void {
    // Remove other financial statement sections (cleanup)
    removeSections([
      DOM_SELECTORS.INCOME_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_2020_2023,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_2020_2023,
      DOM_SELECTORS.CASH_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1323,
    ]);

    // Create and inject balance sheet images
    const balanceHTML = createImageHTML(
      IMAGE_PATHS.BALANCE_1925,
      'balance-sheet-section-1925'
    );

    // Inject content with defensive check
    const success = injectContent(DOM_SELECTORS.MAIN_SECTION, balanceHTML);
    if (!success) {
      console.error('[Range1925Strategy] Failed to inject balance sheet content - .main-section not found');
    }
  }

  /**
   * Apply Cash Flow content (no period type variation for 2019-2025)
   */
  private applyCashFlowContent(): void {
    // Remove other financial statement sections (cleanup)
    removeSections([
      DOM_SELECTORS.INCOME_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_2020_2023,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_2020_2023,
      DOM_SELECTORS.CASH_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1323,
    ]);

    // Create and inject cash flow images
    const cashFlowHTML = createImageHTML(
      IMAGE_PATHS.CASH_1925,
      'cash-flow-section-1925'
    );

    // Inject content with defensive check
    const success = injectContent(DOM_SELECTORS.MAIN_SECTION, cashFlowHTML);
    if (!success) {
      console.error('[Range1925Strategy] Failed to inject cash flow content - .main-section not found');
    }
  }

  /**
   * Hide all content for unknown tabs and show "No data available" message
   */
  private hideAllContent(): void {
    hideElements([
      DOM_SELECTORS.KEY_FINANCIALS,
      DOM_SELECTORS.FINANCIALS_TABLE_CONTAINER,
      DOM_SELECTORS.FOOTNOTE,
      DOM_SELECTORS.CAPITALIZATION_SECTION,
      DOM_SELECTORS.VALUATION_SECTION,
    ]);

    removeSections([
      DOM_SELECTORS.CAP_SECTION_2020_2023,
      DOM_SELECTORS.VAL_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.INCOME_SECTION_1323,
      DOM_SELECTORS.BALANCE_SECTION_2020_2023,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_1323,
      DOM_SELECTORS.CASH_SECTION_2020_2023,
      DOM_SELECTORS.CASH_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1323,
    ]);

    // Show "No data available" message
    const mainSection = document.querySelector(DOM_SELECTORS.MAIN_SECTION);
    if (mainSection) {
      mainSection.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; min-height: 400px;">
          <p style="font-size: 16px; color: #666; text-align: center; font-family: 'Times New Roman', Times, serif;">
            No data available for this timeline range and period type.
          </p>
        </div>
      `;
    }
  }
}
